import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { useUpdateCompanyParametersBO } from "@/api/company.api";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  ParametersPaymentTermFormData,
  ParametersPaymentTermSchema,
} from "@/types/forms/bo/clientsParameters.form";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";

type UpdatePaymentTermModalProps = {
  closeModal: () => void;
  paymentTerm: number;
  title: string;
};

export const UpdatePaymentTermModal = ({
  closeModal,
  paymentTerm,
  title,
}: UpdatePaymentTermModalProps) => {
  const { t } = useTranslation("boClients");
  const [companyId] = useLocation().pathname.split("/").slice(-1);

  const { mutateAsync: updateCompanyParametersBO, isPending } = useUpdateCompanyParametersBO();

  const paymentTermForm = useForm<ParametersPaymentTermFormData>({
    resolver: zodResolver(ParametersPaymentTermSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      paymentTerm: paymentTerm,
    },
  });

  const onSubmit: SubmitHandler<ParametersPaymentTermFormData> = async (data) => {
    const ret = await updateCompanyParametersBO({
      id: companyId,
      payload: data,
    });
    if (ret?.status === 200) {
      closeModal();
    }
  };

  const handleReset = async () => {
    const ret = await updateCompanyParametersBO({
      id: companyId,
      payload: { paymentTerm: 0 },
    });
    if (ret?.status === 200) {
      closeModal();
    }
  };

  return (
    <Modal
      open
      onClose={closeModal}
      title={title}
      size="sm"
      contentClassName="max-w-[480px]"
      footer={
        <Button type="submit" form="paymentTermForm" disabled={isPending} className="w-full">
          {isPending ? <StyledSpinner /> : t("paymentTermModal.submit")}
        </Button>
      }
    >
      <form
        id="paymentTermForm"
        onSubmit={paymentTermForm.handleSubmit(onSubmit)}
        className="flex flex-col gap-4 pt-2"
      >
        <ControlledUnifiedInput
          name="paymentTerm"
          control={paymentTermForm.control}
          defaultValue={paymentTerm}
          inputType="integer"
          placeholder={t("paymentTermModal.paymentTermPlaceholder")}
        />
        <div className="flex items-center gap-2">
          <hr className="w-full" />
          <p className="text-sm text-gray-500">{t("paymentTermModal.or")}</p>
          <hr className="w-full" />
        </div>
        <Button type="button" variant="ghost" onClick={handleReset} disabled={isPending}>
          {isPending ? <StyledSpinner /> : t("paymentTermModal.reset")}
        </Button>
      </form>
    </Modal>
  );
};
